import { z } from 'zod';
import { KmCorporateBodySchema, KmPlaceSchema } from './km_refund_wire.js';

const Instant = z.iso.datetime({ offset: true });

/** `GET /km-refund/transit`: o trajeto aberto (check-in feito, check-out não) de quem pede. */
export const KmTransitSchema = z.discriminatedUnion('inTransit', [
  z.object({ inTransit: z.literal(false) }).strict(),
  z
    .object({
      inTransit: z.literal(true),
      from: KmPlaceSchema,
      /** Hora do check-in gravada pelo servidor. */
      startedAt: Instant,
    })
    .strict(),
]);
export type KmTransit = z.infer<typeof KmTransitSchema>;

/** `POST /km-refund/transit/quote`: o KM e o valor do trajeto aberto até a chegada. */
export const KmTransitQuoteSchema = z
  .object({
    from: KmPlaceSchema,
    to: KmPlaceSchema,
    startedAt: Instant,
    /** A hora do check-out que o servidor carimbaria agora. */
    endedAt: Instant,
    calculatedKm: z.number(),
    manualKm: z.number().nullable(),
    /** KM que vale (o manual se houver), 2 casas. */
    km: z.number(),
    valuePerKm: z.number(),
    amount: z.number(),
    /** Calculado = 0 e sem KM manual: não há rota, o check-out não sai. */
    routeUnavailable: z.boolean(),
    /** KM manual com `setOutsidePolicyManualEditing` ligado na política. O GPS não entra: é
     *  evidência para o gestor, no voucher. */
    outOfPolicy: z.boolean(),
  })
  .strict();
export type KmTransitQuote = z.infer<typeof KmTransitQuoteSchema>;

// Corpos tolerantes, como os do manual: um web-chat mais novo que o BFF não morre no 400.

/** `POST /km-refund/transit`: o check-in. Sem viajante: o rastreado é sempre de quem pede (o
 *  `expensesSaveCheckIn` não recebe viajante). */
export const KmCheckInBodySchema = z.looseObject({
  /** O ponto do aparelho no check-in; `null` = sem localização. */
  here: KmPlaceSchema.nullable(),
  /** A partida que a pessoa confirmou (o próprio `here` ou outro endereço). */
  departure: KmPlaceSchema,
  // `z.object` descarta chave desconhecida em vez de repassar: um `travelerId` não chega ao BFF.
  corporate: z.object(KmCorporateBodySchema.omit({ travelerId: true }).shape),
});
export type KmCheckInBody = z.infer<typeof KmCheckInBodySchema>;

/** O que o app nativo enviou até o check-out: o expenses-api confere se chegou tudo. */
const KmTrackingSchema = z.looseObject({
  pointCount: z.number().int().nonnegative(),
  lastPointAt: Instant.nullable(),
});

export const KmQuoteBodySchema = z.looseObject({
  /** O ponto do aparelho na chegada; `null` = sem localização. */
  here: KmPlaceSchema.nullable(),
  /** A chegada que a pessoa confirmou. */
  arrival: KmPlaceSchema,
  manualKm: z.number().positive().max(10000).optional(),
  tracking: KmTrackingSchema.optional(),
});
export type KmQuoteBody = z.infer<typeof KmQuoteBodySchema>;

/** `POST /km-refund/transit/finish`: o check-out. `times` só quando a pessoa editou a hora. */
export const KmCheckOutBodySchema = KmQuoteBodySchema.extend({
  times: z.looseObject({ departure: Instant, arrival: Instant }).optional(),
});
export type KmCheckOutBody = z.infer<typeof KmCheckOutBodySchema>;

/** A resposta do check-out (202): enviado com a cotação usada, ou incerto. Incerto = o
 *  `expensesSaveCheckOut` falhou depois de sair; a tela manda conferir os pedidos antes de tentar
 *  de novo, porque ele não é idempotente. */
export const KmCheckOutResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('submitted'), quote: KmTransitQuoteSchema }).strict(),
  z.object({ status: z.literal('indeterminate') }).strict(),
]);
export type KmCheckOutResult = z.infer<typeof KmCheckOutResultSchema>;

// [lat, lng, epochMs, precisão em m]: compacto porque o WAF interno de prod recusa corpo acima de
// 8 KB; 100 pontos assim ficam perto de 4 KB.
const TrackPointSchema = z.tuple([
  z.number().min(-90).max(90),
  z.number().min(-180).max(180),
  z.number().int().positive(),
  z.number().nonnegative(),
]);

/** `POST /km-refund/transit/points`: um lote do GPS do app nativo. Tolerante, como os outros
 *  corpos. */
export const KmTrackPointsBodySchema = z.looseObject({
  points: z.array(TrackPointSchema).min(1).max(100),
});
